import { FiChevronRight, FiGrid, FiLogOut, FiX } from 'react-icons/fi';
import { Link } from '@tanstack/react-router';
import { cn } from '@/shared/lib/cn';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/shared/ui/sheet';
import { renderIcon } from '@/shared/layout/icon-registry';
import {
  accountPanelAvatarClasses,
  accountPanelCloseClasses,
  accountPanelFooterClasses,
  accountPanelGridClasses,
  accountPanelGridIconClasses,
  accountPanelIdentityClasses,
  accountPanelIdentityTopRowClasses,
  accountPanelMenuClasses,
  accountPanelNameClasses,
  accountPanelRoleClasses,
  accountPanelRowBodyClasses,
  accountPanelRowChevronClasses,
  accountPanelRowClasses,
  accountPanelRowDescriptionClasses,
  accountPanelRowIconClasses,
  accountPanelRowIconWrapClasses,
  accountPanelRowLabelClasses,
  accountPanelSectionLabelClasses,
  accountPanelSheetClasses,
  accountPanelSignOutClasses,
  accountPanelSignOutIconClasses,
  accountPanelTileClasses,
  accountPanelTileIconClasses,
  accountPanelTileIconWrapClasses,
  accountPanelTileLabelClasses,
  accountPanelTileTints,
  accountPanelWorkspaceClasses,
} from '@/shared/layout/profile-panel/styles';
import { deriveInitials } from '@/shared/layout/profile-panel/parts/initials';
import type {
  AccountPanelGroup,
  AccountPanelMenuItem,
  AccountPanelProps,
} from '@/shared/layout/profile-panel/types';

/**
 * The account panel: a full-height column anchored to the right edge.
 *
 * WHY A PANEL, NOT A DROPDOWN
 *
 * A dropdown is the default shape for an account menu and it is the wrong one.
 * When someone opens their account menu they are usually mid-task and looking
 * for something they could not find in the navigation. A dropdown gives them
 * three grey rows and a Log out. A panel can afford to show them who they are
 * — genuinely ambiguous on a shared admin login — and where the destinations
 * they actually reach for live.
 *
 * WHY IT IS A COLUMN AND NOT A SECOND SIDEBAR
 *
 * The two are different in kind, not just in position. The sidebar is a rail of
 * 36px rows listing every destination in the product. This is a column for one
 * person: a tinted identity field, then a grid of tiles for the places they
 * jump to, then described rows for the things that configure the space. Two
 * reading modes — recognition for jumping, reading for deciding — and neither
 * one is a narrower copy of the navigation.
 *
 * It is a sheet, not a hand-rolled div, so focus trapping, Escape, focus
 * restore and the `aria-modal` announcement come from one place that is
 * already correct.
 *
 * The panel renders even when `open` is false — the sheet owns its own
 * mount/unmount and its closing transition.
 */
export function AccountPanel({
  open,
  onOpenChange,
  identity,
  jumpTo = [],
  groups = [],
  onSignOut,
}: AccountPanelProps) {
  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange} side="right">
      <SheetContent className={accountPanelSheetClasses} aria-label="Account">
        <div className={accountPanelMenuClasses}>
          {renderIdentity(identity)}
          {renderJumpTo(jumpTo, close)}
          {renderGroups(groups, close)}
        </div>
        {renderFooter(onSignOut, close)}
      </SheetContent>
    </Sheet>
  );
}

/**
 * The identity field.
 *
 * This is the panel's one loud element. Everything below it is deliberate
 * quiet, which is what lets the accent field announce the panel without a
 * heading that says "Account".
 */
function renderIdentity(identity: AccountPanelProps['identity']) {
  return (
    <div className={accountPanelIdentityClasses}>
      <div className={accountPanelIdentityTopRowClasses}>
        <span className={accountPanelAvatarClasses} aria-hidden="true">
          {identity.initials ?? deriveInitials(identity.name)}
        </span>
        <SheetClose className={accountPanelCloseClasses} aria-label="Close account panel">
          <FiX className="h-4 w-4" aria-hidden="true" />
        </SheetClose>
      </div>
      <SheetTitle className={accountPanelNameClasses}>{identity.name}</SheetTitle>
      <p className={accountPanelRoleClasses}>{identity.role}</p>
      <span className={accountPanelWorkspaceClasses}>
        <FiGrid className={accountPanelGridIconClasses} aria-hidden="true" />
        <span className="truncate">{identity.workspaceName}</span>
      </span>
    </div>
  );
}

/**
 * The tile grid.
 *
 * Tint is chosen by position, so a tile keeps its colour while its position is
 * stable and the user can learn "Tenants is the blue one". Tints repeat when
 * the list is longer than the palette, which is fine — they are wayfinding
 * cues, not identifiers.
 */
function renderJumpTo(items: ReadonlyArray<AccountPanelMenuItem>, onNavigate: () => void) {
  if (items.length === 0) return null;

  return (
    <div>
      <span className={accountPanelSectionLabelClasses}>Jump to</span>
      <div className={accountPanelGridClasses}>
        {items.map((item, index) => (
          <Tile
            key={item.to}
            item={item}
            tint={accountPanelTileTints[index % accountPanelTileTints.length] ?? accountPanelTileTints[0]}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
}

function Tile({
  item,
  tint,
  onNavigate,
}: {
  item: AccountPanelMenuItem;
  tint: (typeof accountPanelTileTints)[number];
  onNavigate: () => void;
}) {
  return (
    <Link to={item.to} onClick={onNavigate} className={accountPanelTileClasses} title={item.description}>
      <span className={cn(accountPanelTileIconWrapClasses, tint.wrap)}>
        {renderIcon(item.icon, accountPanelTileIconClasses)}
      </span>
      <span className={accountPanelTileLabelClasses}>{item.label}</span>
    </Link>
  );
}

/** Described rows — for destinations whose label alone does not say enough. */
function renderGroups(groups: ReadonlyArray<AccountPanelGroup>, onNavigate: () => void) {
  if (groups.length === 0) return null;

  return groups.map((group) => (
    <div key={group.label}>
      <span className={accountPanelSectionLabelClasses}>{group.label}</span>
      {group.items.map((item) => (
        <Row key={item.to} item={item} onNavigate={onNavigate} />
      ))}
    </div>
  ));
}

function Row({
  item,
  onNavigate,
}: {
  item: AccountPanelMenuItem;
  onNavigate: () => void;
}) {
  return (
    <Link to={item.to} onClick={onNavigate} className={accountPanelRowClasses}>
      <span className={accountPanelRowIconWrapClasses}>
        {renderIcon(item.icon, accountPanelRowIconClasses)}
      </span>
      <span className={accountPanelRowBodyClasses}>
        <span className={accountPanelRowLabelClasses}>{item.label}</span>
        <span className={accountPanelRowDescriptionClasses}>{item.description}</span>
      </span>
      <FiChevronRight className={accountPanelRowChevronClasses} aria-hidden="true" />
    </Link>
  );
}

function renderFooter(onSignOut: AccountPanelProps['onSignOut'], onNavigate: () => void) {
  if (!onSignOut) return null;

  return (
    <div className={accountPanelFooterClasses}>
      <button
        type="button"
        onClick={() => {
          onNavigate();
          onSignOut();
        }}
        className={accountPanelSignOutClasses}
      >
        <FiLogOut className={accountPanelSignOutIconClasses} aria-hidden="true" />
        Sign out
      </button>
    </div>
  );
}

export type {
  AccountPanelGroup,
  AccountPanelMenuItem,
  AccountPanelProps,
  ProfileIdentity,
} from '@/shared/layout/profile-panel/types';
